import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext';
import Header from "../../layouts/Header";
import { PremiumFeatureLock } from '../../components/premium/PremiumFeatureLock';
import { Card, Row, Col, Alert, Spinner, Badge, Nav, Tab, ProgressBar } from 'react-bootstrap';
import { getDataOnRealtimeDb } from '../../utils/firebase';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { useBudget } from '../../hooks/useBudget';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

export default function SmartInsights() {
    const { currentUser, subscription } = useAuth();
    const isPremium = subscription === 'premium';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [insights, setInsights] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    const {
        calculateSpent,
        budgets
    } = useBudget();

    useEffect(() => {
        if (isPremium && budgets) {
            fetchInsights();
        }
    }, [currentUser, isPremium, budgets]);

    const formatFirebaseData = (data) => {
        if (!data) return [];
        return Object.entries(data).map(([id, item]) => ({
            id,
            ...item
        }));
    };

    const formatGoalData = (goals) => {
        if (!goals) return [];
        return Object.entries(goals).map(([id, goal]) => ({
            id,
            name: goal.name || 'Unnamed Goal',
            target_amount: parseFloat(goal.targetAmount) || 0,
            current_amount: parseFloat(goal.currentAmount) || 0,
            deadline: goal.targetDate || null,
            ...goal
        }));
    };

    const formatBillData = (bills) => {
        if (!bills) return [];
        return Object.entries(bills).map(([id, bill]) => ({
            id,
            name: bill.name || 'Unnamed Bill',
            amount: parseFloat(bill.amount) || 0,
            due_date: bill.dueDate || null,
            status: bill.status || 'pending',
            ...bill
        }));
    };

    const formatBudgetData = React.useCallback((budget) => {
        if (!budget || !calculateSpent) {
            console.log('No budget data or calculateSpent not available');
            return [];
        }
        
        try {
            const budgetsWithSpent = calculateSpent();
            
            if (!budgetsWithSpent || !Array.isArray(budgetsWithSpent)) {
                console.log('Invalid budgets data:', budgetsWithSpent);
                return [];
            }

            const formatted = budgetsWithSpent.map((item) => {
                if (!item) return null;
                return {
                    id: item.id,
                    category: item.category || 'Uncategorized',
                    amount: parseFloat(item.amount) || 0,
                    actual_spending: parseFloat(item.spent) || 0,
                    period_start: item.startDate || null,
                    period_end: item.endDate || null,
                };
            }).filter(Boolean);

            return formatted;
        } catch (error) {
            console.error('Error formatting budget data:', error);
            return [];
        }
    }, [calculateSpent]);

    const fetchInsights = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all required data
            const [expenses, income, goals, bills] = await Promise.all([
                getDataOnRealtimeDb(`users/${currentUser.uid}/expenses`),
                getDataOnRealtimeDb(`users/${currentUser.uid}/income`),
                getDataOnRealtimeDb(`users/${currentUser.uid}/goals`),
                getDataOnRealtimeDb(`users/${currentUser.uid}/bills`)
            ]);

            // Use budgets from the hook instead of fetching again
            const formattedData = {
                expenses: formatFirebaseData(expenses),
                income: formatFirebaseData(income),
                budget: formatBudgetData(budgets),
                goals: formatGoalData(goals),
                bills: formatBillData(bills)
            };

            console.log('Sending data to API:', formattedData);

            // Get insights from Python service
            const response = await fetch('http://127.0.0.1:5000/api/insights', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formattedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate insights');
            }

            const insightsData = await response.json();
            console.log('Received insights data:', insightsData);
            setInsights(insightsData);

        } catch (error) {
            console.error('Error fetching insights:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, formatBudgetData, budgets]);

    const renderInsightCard = (title, insights, type) => (
        <Card className="mb-4">
            <Card.Header>
                <Card.Title as="h6">
                    <i className={`ri-${getIconForType(type)} me-2`}></i>
                    {title}
                </Card.Title>
            </Card.Header>
            <Card.Body>
                {insights.map((insight, index) => (
                    <div key={index} className="d-flex align-items-center mb-3">
                        <Badge bg={insight.severity} className="me-2">
                            {insight.type}
                        </Badge>
                        <span>{insight.message}</span>
                    </div>
                ))}
            </Card.Body>
        </Card>
    );

    const renderSpendingChart = (data) => {
        if (!data || !data.monthly_spending) return null;

        const chartData = {
            labels: Object.keys(data.monthly_spending),
            datasets: [{
                label: 'Monthly Spending',
                data: Object.values(data.monthly_spending),
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1
            }]
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">Monthly Spending Trend</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Line data={chartData} options={{ responsive: true }} />
                </Card.Body>
            </Card>
        );
    };

    const renderCategoryDistribution = (data) => {
        if (!data || !data.category_distribution) return null;

        const chartData = {
            labels: Object.keys(data.category_distribution),
            datasets: [{
                data: Object.values(data.category_distribution),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40'
                ]
            }]
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">Spending by Category</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Doughnut data={chartData} options={{ responsive: true }} />
                </Card.Body>
            </Card>
        );
    };

    const renderBudgetAnalysis = (budgetAnalysis) => {
        if (!budgetAnalysis || budgetAnalysis.length === 0) {
            return (
                <Card className="mb-4">
                    <Card.Header>
                        <Card.Title as="h6">Budget vs Actual</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Alert variant="info">
                            No budget data available. Set up your budget to track spending against your targets.
                        </Alert>
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">Budget vs Actual</Card.Title>
                </Card.Header>
                <Card.Body>
                    {budgetAnalysis.map((item, index) => {
                        const progressPercentage = Math.min(100, (item.actual_spending / item.budget_amount) * 100);
                        const overBudgetPercentage = item.status === 'over_budget' ? 
                            ((item.actual_spending - item.budget_amount) / item.budget_amount) * 100 : 0;

                        return (
                            <div key={index} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span>{item.category}</span>
                                    <span className={`text-${item.status === 'over_budget' ? 'danger' : item.status === 'under_budget' ? 'success' : 'info'}`}>
                                        ${item.actual_spending.toFixed(2)} / ${item.budget_amount.toFixed(2)}
                                    </span>
                                </div>
                                <ProgressBar>
                                    <ProgressBar 
                                        variant={item.status === 'over_budget' ? 'danger' : 'success'} 
                                        now={progressPercentage} 
                                        key={1}
                                    />
                                    {item.status === 'over_budget' && (
                                        <ProgressBar 
                                            variant="danger" 
                                            now={overBudgetPercentage}
                                            key={2} 
                                        />
                                    )}
                                </ProgressBar>
                                <small className="text-muted">
                                    {item.status === 'over_budget' 
                                        ? `${Math.abs(item.variance_percentage)}% over budget` 
                                        : item.status === 'under_budget' 
                                            ? `${Math.abs(item.variance_percentage)}% under budget`
                                            : 'On budget'}
                                </small>
                            </div>
                        );
                    })}
                </Card.Body>
            </Card>
        );
    };

    const renderGoalsProgress = (goalsAnalysis) => {
        if (!goalsAnalysis || goalsAnalysis.length === 0) return null;

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">Financial Goals Progress</Card.Title>
                </Card.Header>
                <Card.Body>
                    {goalsAnalysis.map((goal, index) => (
                        <div key={index} className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span>{goal.name}</span>
                                <Badge bg={goal.status === 'completed' ? 'success' : goal.status === 'on_track' ? 'info' : 'warning'}>
                                    {goal.status.replace('_', ' ')}
                                </Badge>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                                <small>${goal.current_amount} of ${goal.target_amount}</small>
                                <small>{goal.time_remaining_days ? `${goal.time_remaining_days} days left` : 'No deadline'}</small>
                            </div>
                            <ProgressBar 
                                variant={goal.status === 'completed' ? 'success' : goal.status === 'on_track' ? 'info' : 'warning'}
                                now={goal.progress_percentage} 
                                label={`${Math.round(goal.progress_percentage)}%`}
                            />
                        </div>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    const renderBillsOverview = (billsAnalysis) => {
        if (!billsAnalysis || billsAnalysis.length === 0) return null;

        const groupedBills = {
            overdue: billsAnalysis.filter(bill => bill.status === 'overdue'),
            due_soon: billsAnalysis.filter(bill => bill.status === 'due_soon'),
            upcoming: billsAnalysis.filter(bill => bill.status === 'upcoming')
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">Bills Overview</Card.Title>
                </Card.Header>
                <Card.Body>
                    {Object.entries(groupedBills).map(([status, bills]) => bills.length > 0 && (
                        <div key={status} className="mb-3">
                            <h6 className="text-capitalize mb-2">{status.replace('_', ' ')}</h6>
                            {bills.map((bill, index) => (
                                <Alert key={index} variant={status === 'overdue' ? 'danger' : status === 'due_soon' ? 'warning' : 'info'}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{bill.name}</span>
                                        <span>${bill.amount}</span>
                                    </div>
                                    <small>Due: {bill.due_date}</small>
                                </Alert>
                            ))}
                        </div>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    const getIconForType = (type) => {
        const icons = {
            'spending_patterns': 'line-chart-line',
            'category_analysis': 'pie-chart-line',
            'trend_analysis': 'trending-up-line',
            'budget_recommendations': 'money-dollar-circle-line',
            'savings_opportunities': 'piggy-bank-line',
            'risk_alerts': 'alert-line',
            'bills': 'bill-line',
            'goals': 'flag-line'
        };
        return icons[type] || 'information-line';
    };

    if (!isPremium) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="main-title mb-0">Smart Insights</h4>
                    </div>

                    <PremiumFeatureLock 
                        title="AI-Powered Financial Insights"
                        description="Get personalized financial insights and recommendations using advanced AI analysis. Upgrade to premium to access this feature."
                    />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 className="main-title mb-0">Smart Insights</h4>
                    <button className="btn btn-primary" onClick={fetchInsights}>
                        <i className="ri-refresh-line me-2"></i>
                        Refresh Insights
                    </button>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-3">Generating insights...</p>
                    </div>
                ) : insights && (
                    <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                        <Nav variant="tabs" className="mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey="overview">Overview</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="budget">Budget & Goals</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="bills">Bills</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="recommendations">Recommendations</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="overview">
                                <Row>
                                    <Col md={8}>
                                        {renderSpendingChart(insights)}
                                        {renderInsightCard('Key Insights', insights.key_insights, 'trend_analysis')}
                                    </Col>
                                    <Col md={4}>
                                        {insights.savings_analysis && (
                                            <Card className="mb-4">
                                                <Card.Header>
                                                    <Card.Title as="h6">Savings Rate</Card.Title>
                                                </Card.Header>
                                                <Card.Body>
                                                    <h3 className="mb-3">{insights.savings_analysis.current_savings_rate}%</h3>
                                                    <ProgressBar 
                                                        variant={insights.savings_analysis.current_savings_rate >= insights.savings_analysis.target_savings_rate ? 'success' : 'warning'}
                                                        now={insights.savings_analysis.current_savings_rate} 
                                                        label={`${insights.savings_analysis.current_savings_rate}%`}
                                                    />
                                                    <small className="text-muted mt-2 d-block">
                                                        Target: {insights.savings_analysis.target_savings_rate}%
                                                    </small>
                                                </Card.Body>
                                            </Card>
                                        )}
                                        {renderCategoryDistribution(insights)}
                                    </Col>
                                </Row>
                            </Tab.Pane>

                            <Tab.Pane eventKey="budget">
                                <Row>
                                    <Col md={6}>
                                        {insights.budget_analysis && insights.budget_analysis.length > 0 ? (
                                            renderBudgetAnalysis(insights.budget_analysis)
                                        ) : (
                                            <Card className="mb-4">
                                                <Card.Header>
                                                    <Card.Title as="h6">Budget vs Actual</Card.Title>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Alert variant="info">
                                                        <Alert.Heading>No Budget Analysis Available</Alert.Heading>
                                                        <p>This could be because:</p>
                                                        <ul>
                                                            <li>You haven't set up any budget categories yet</li>
                                                            <li>There are no expenses in the current month to compare against</li>
                                                            <li>The budget data format might need to be updated</li>
                                                        </ul>
                                                        <p>Please ensure you have:</p>
                                                        <ul>
                                                            <li>Set up budget categories with amounts</li>
                                                            <li>Categorized your expenses</li>
                                                            <li>Have expenses in the current month</li>
                                                        </ul>
                                                    </Alert>
                                                </Card.Body>
                                            </Card>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {renderGoalsProgress(insights.goals_analysis)}
                                    </Col>
                                </Row>
                            </Tab.Pane>

                            <Tab.Pane eventKey="bills">
                                <Row>
                                    <Col md={12}>
                                        {renderBillsOverview(insights.bills_analysis)}
                                    </Col>
                                </Row>
                            </Tab.Pane>

                            <Tab.Pane eventKey="recommendations">
                                <Row>
                                    <Col md={12}>
                                        {insights.optimization_suggestions.map((suggestion, index) => (
                                            <Alert key={index} variant={suggestion.priority === 'high' ? 'danger' : 'warning'} className="mb-3">
                                                <Alert.Heading>
                                                    <i className={`ri-${getIconForType(suggestion.type)} me-2`}></i>
                                                    {suggestion.title}
                                                </Alert.Heading>
                                                <p>{suggestion.description}</p>
                                                {suggestion.potential_savings && (
                                                    <div className="mt-2">
                                                        <strong>Potential Savings: </strong>
                                                        ${suggestion.potential_savings}
                                                    </div>
                                                )}
                                                {suggestion.required_monthly && (
                                                    <div className="mt-2">
                                                        <strong>Required Monthly Contribution: </strong>
                                                        ${suggestion.required_monthly}
                                                    </div>
                                                )}
                                            </Alert>
                                        ))}
                                    </Col>
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                )}
            </div>
        </React.Fragment>
    );
} 